import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Modal } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import TrashIcon from '@rsuite/icons/Trash';
import EditIcon from '@rsuite/icons/Edit';
import VisibleIcon from '@rsuite/icons/Visible';
import CloseIcon from '@rsuite/icons/Close';
import { toggleModal } from '../../../reducers/modalReducer';
import { setFileReport, setCurrentScore, setLogList, setRModalData, setOpenRModal, updateFileAllocation } from '../../../reducers/filesAllocationReducer';
import socket, { postRequest, showMessage, getRequest } from '../../../config';
import AutoFillStepper from '../../autoFill/AutoFillStepper';
import Send from '../../../../images/dataRepo/send.svg';
import Search from '../../../../images/dataRepo/search.svg';
import { Divider } from '@mui/material';

const btnCancel = { background: 'none', border: '1.5px solid #9CA3AF', borderRadius: '6px', cursor: 'pointer', color: '#6B7280', fontSize: '13px', fontWeight: 500, padding: '0.3rem 1.2rem' };
const btnMinimize = { background: 'none', border: '1.5px solid #3B82F6', borderRadius: '6px', cursor: 'pointer', color: '#3B82F6', fontSize: '13px', fontWeight: 500, padding: '0.3rem 1.2rem' };

const AutoFillStepperModal = () => {
    const dispatch = useDispatch();
    const store = useStore();
    const modal = useSelector(state => state.modal);
    const { fileReport, currentScore, tab, autoFillContext, company: reduxCompany } = useSelector(state => state.fileAllocation);
    const { selectedCompany: company } = useSelector(state => state.company);
    const [indexingProgress, setIndexingProgress] = useState({ total: 0, current: 0, currentFile: '' });
    const [allocationProgress, setAllocationProgress] = useState({ total: 0, current: 0, fileName: '', matchedQNo: '', matchedQuestion: '' });
    const [savingProgress, setSavingProgress] = useState({ total: 0, current: 0, fileName: '', qNo: '' });

    // File preview state (allocation step)
    const [previewFile, setPreviewFile] = useState(null);
    const [aiPopover, setAiPopover] = useState(null); // { key, qNo, x, y, pinned }
    const aiPopoverPinned = aiPopover?.pinned || false;

    // Hidden file input ref (select step)
    const fileInputRef = useRef(null);

    const phase = modal.autoFillStepperPhase || '';
    const isOpen = modal.open === 'autoFillStepper';


    // Scope display info
    const scopeName = modal.autoFillModuleName || autoFillContext?.scopeName || '';
    const scope = autoFillContext?.scope || 'module';
    const scopeLabel = scope === 'section' ? 'Section' : scope === 'module' ? '(All)' : '';
    const moduleHeading = company?.modules?.[autoFillContext?.moduleType]?.heading || '';
    const headerInfo = scopeName ? `Auto-filling: ${scopeName}${scopeLabel ? ' ' + scopeLabel : ''}${scope === 'section' && moduleHeading ? ' — ' + moduleHeading : ''}` : '';

    // ── Socket listeners for indexing & allocation progress ──
    useEffect(() => {
        const onIndexingProgress = (data) => {
            setIndexingProgress({ total: data.total || 0, current: data.current || 0, currentFile: data.currentFile || '' });
        };
        const onAllocationProgress = (data) => {
            setAllocationProgress({ total: data.total || 0, current: data.current || 0, fileName: data.fileName || '', matchedQNo: data.matchedQNo || '', matchedQuestion: data.matchedQuestion || '', confidence: data.confidence || 0, reasoning: data.reasoning || '' });
        };
        const onAllocationCompleted = (data) => {
            setIndexingProgress({ total: 0, current: 0, currentFile: '' });
            setAllocationProgress({ total: 0, current: 0, fileName: '', matchedQNo: '', matchedQuestion: '' });
            const currentPhase = store.getState().modal.autoFillStepperPhase;
            const currentOpen = store.getState().modal.open;
            // Always store fileReport so handleExpand (maximize from status bar) can use it
            if (data?.fileReport) dispatch(updateFileAllocation({ fileReport: data.fileReport }));
            // Only advance the stepper if it's currently open at indexing phase
            if (data?.fileReport && currentPhase === 'indexing' && currentOpen === 'autoFillStepper') dispatch(toggleModal({ open: 'autoFillStepper', autoFillStepperPhase: 'allocation', autoFillStatusBarPhase: 'allocated' }));
        };
        const onSavingProgress = (data) => {
            setSavingProgress({ total: data.total || 0, current: data.current || 0, fileName: data.fileName || '', qNo: data.qNo || '' });
        };
        const onSavingCompleted = () => {
            setSavingProgress({ total: 0, current: 0, fileName: '', qNo: '' });
            // In Filing Assistant mode (no auto-fill), close the stepper cleanly after processing
            const state = store.getState();
            if (!state.modal.autoFillWithFilling) {
                dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '', autoFillJustCompleted: true }));
            }
        };
        socket.on('indexing:progress', onIndexingProgress);
        socket.on('allocation:progress', onAllocationProgress);
        socket.on('allocation:completed', onAllocationCompleted);
        socket.on('processingFiles:progress', onSavingProgress);
        socket.on('processingFiles:completed', onSavingCompleted);
        return () => {
            socket.off('indexing:progress', onIndexingProgress);
            socket.off('allocation:progress', onAllocationProgress);
            socket.off('allocation:completed', onAllocationCompleted);
            socket.off('processingFiles:progress', onSavingProgress);
            socket.off('processingFiles:completed', onSavingCompleted);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Allocation step helpers (copied from importSummary) ──

    const handleFilePreview = (file) => {
        const url = file.location || file.url;
        if (!url) return;
        setPreviewFile({ name: file.originalname || file.title, url });
    };

    const getSectionNameFromSection = (section, qNo) => {
        if (section.sectionNo == qNo || section.no == qNo) {
            const name = section.sectionName || section.name || '';
            return { displayName: name, sectionOnly: name };
        }
        if (section.questions) {
            for (const question of Object.values(section.questions)) {
                if (question.qNo == qNo) {
                    const sectionOnly = section.sectionName || section.name || '';
                    return { displayName: question.qDescription || sectionOnly, sectionOnly };
                }
            }
        }
        if (section.subFolders) {
            for (const subFolder of Object.values(section.subFolders)) {
                const result = getSectionNameFromSection(subFolder, qNo);
                if (result) return result;
            }
        }
        return null;
    };

    const getSectionName = (module, qNo, file, key) => {
        if (qNo == "DXXX" || qNo == "SXXX") return {
            displayName: <div
                onClick={() => handleEditDispatch({ key, fileName: file.originalname, from: 'fileReport', actionType: "move", fileData: file, index: "0" })}
                className='bg62 br10px df aic p1rem jcsb fsi'
            >
                <p>Please select section</p>
                <KeyboardArrowDown className='c3' />
            </div>,
            sectionOnly: ''
        };
        const effectiveCompany = company || reduxCompany;
        const module2 = module || (effectiveCompany?.modules ? { ...effectiveCompany.modules[tab || "repository"] } : null);
        const sections = [...(module2?.sections || [])];
        for (const section of sections) {
            const result = getSectionNameFromSection(section, qNo);
            if (result) return { displayName: `${qNo} ${result.displayName}`, sectionOnly: result.sectionOnly };
        }
        const entry = fileReport?.importResult && Object.values(fileReport.importResult).find(
            r => r.section?.includes(qNo) && r.sectionName
        );
        if (entry?.allocatedSection) return { displayName: `${qNo} ${entry.allocatedSection}`, sectionOnly: entry.sectionName || '' };
        if (entry?.sectionName) return { displayName: `${qNo} ${entry.sectionName}`, sectionOnly: entry.sectionName };
        return { displayName: "Unknown", sectionOnly: '' };
    };

    const handleEditDispatch = (action) => {
        dispatch(setRModalData({ ...action }));
        dispatch(setOpenRModal(true));
    };

    function isSubarray(subarray, array) {
        const sortedSubarray = subarray.slice().sort();
        const sortedArray = array.slice().sort();
        let i = 0;
        return sortedArray.some((element) => {
            if (element === sortedSubarray[i]) i++;
            return i === sortedSubarray.length;
        });
    }

    const calcAccuracyScore = (count) => {
        let allocated = 0;
        for (const key of Object.keys(fileReport.importResult)) {
            const currentSection = fileReport.importResult[key]['section'];
            const oldSection = fileReport.importResult[key]['originalSection'];
            if (oldSection.includes("DXXX") || oldSection.includes("SXXX")) continue;
            if (isSubarray(oldSection, currentSection)) allocated++;
        }
        const totalCount = count ?? Object.keys(fileReport.importResult).length;
        dispatch(setCurrentScore(!totalCount ? 0 : Math.round(allocated / totalCount * 100)));
        return !totalCount ? 0 : Math.round(allocated / totalCount * 100);
    };

    const removeSectionFromImport = async (fileKey, index) => {
        const sections = [...fileReport.importResult[fileKey]['section']];
        const qNo = sections[index];
        sections.splice(index, 1);
        const file = fileReport.importResult[fileKey]['file'];

        let newImportResult;
        if (sections.length === 0) {
            const { [fileKey]: _removed, ...rest } = fileReport.importResult;
            newImportResult = rest;
        } else {
            newImportResult = {
                ...fileReport.importResult,
                [fileKey]: { ...fileReport.importResult[fileKey], section: sections }
            };
        }
        const _fileReport = { ...fileReport, importResult: newImportResult };
        const score = calcAccuracyScore(Object.keys(newImportResult).length);
        dispatch(setFileReport(_fileReport));
        const effectiveCompany = company || reduxCompany;
        postRequest(`/company/deleteSection`, {
            companyId: effectiveCompany._id,
            fileReportId: _fileReport['_id'],
            fileKey, index, qNo, score, file
        });
    };

    const getImportResultEntryHTML = (key) => {
        if (!fileReport?.importResult?.[key]) return null;
        const fileName = fileReport.importResult[key].file['originalname'];
        const file = fileReport.importResult[key].file;
        const section = fileReport.importResult[key]['section'];
        const effectiveCompany = company || reduxCompany;
        let module = { ...(effectiveCompany?.modules?.[tab || "repository"] || {}) };
        if (module) module = JSON.parse(JSON.stringify(module));
        const matchScore = fileReport.importResult[key]?.matchScore;
        const reasoning = fileReport.importResult[key]?.reasoning || '';
        const unallocatedReason = fileReport.importResult[key]?.unallocatedReason || '';
        const suggestedQNo = fileReport.importResult[key]?.suggestedQNo || '';
        const suggestedSection = fileReport.importResult[key]?.suggestedSection || '';
        const suggestedQuestion = fileReport.importResult[key]?.suggestedQuestion || '';
        const allocatedSection = fileReport.importResult[key]?.allocatedSection || '';
        if (Array.isArray(section)) {
            return <>
                {section.map((qNo, index) => {
                    const { sectionOnly, displayName: rawDisplayName } = getSectionName(module, qNo, file, key);
                    const isUnallocatedQNo = qNo === 'DXXX' || qNo === 'SXXX';
                    const displayName = (!isUnallocatedQNo && allocatedSection && allocatedSection !== 'Unknown') ? `${qNo} ${allocatedSection}` : rawDisplayName;
                    const isUnallocated = qNo === 'DXXX' || qNo === 'SXXX';
                    // Convert internal 0-1 score to 1-10 scale
                    const scoreVal = matchScore ? Math.round(matchScore * 10) : null;
                    // For unallocated: always red badge. For allocated: green ≥8, orange ≥6, red <6
                    const scoreColor = isUnallocated ? '#dc2626' : (!scoreVal ? '#9CA3AF' : scoreVal >= 8 ? '#16a34a' : scoreVal >= 6 ? '#d97706' : '#dc2626');
                    const scoreLabel = isUnallocated ? 'Low confidence' : (!scoreVal ? '' : scoreVal >= 8 ? 'High' : scoreVal >= 6 ? 'Medium' : 'Low');
                    const showBadge = isUnallocated ? (scoreVal || reasoning || suggestedQNo) : scoreVal;
                    const popoverData = { key, qNo, fileName, reasoning, scoreVal, scoreColor, scoreLabel, isUnallocated, suggestedQNo, suggestedSection, suggestedQuestion };
                    return <React.Fragment key={`${index + section.length - 1}-section-allocate`}>
                        <tr className={`${index == section.length - 1 ? 'fis-tr' : ''}`}>
                            <td className={`tal fis-td mx1rem fs14px fw600 tdu bg1 lightDot ${index != 0 ? 'vh' : ''} ${qNo == "DXXX" ? 'red' : (fileReport['status'] == 'In Progress' ? 'orange' : 'green')}`} style={{ maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <span className="cp" onClick={() => handleFilePreview(file)} data-tooltip={fileName}>{fileName}</span>
                            </td>
                            <td className={`tal mx1rem fs14px fw400 bg1 c15`}>{sectionOnly ? `${qNo} ${sectionOnly}` : sectionOnly}</td>
                            <td className={`tal mx1rem fs14px fw400 bg1 c15`}>{displayName}</td>
                            {fileReport['status'] == 'In Progress' && (
                                <td className={`mx1rem fs1rem fw600 bg1 tal fis-td fis-td-action`}>
                                    <div className="df fdr jcc aic" style={{ gap: '0.6rem' }}>
                                        {showBadge && (
                                            <span
                                                onMouseEnter={e => { const r = e.currentTarget.getBoundingClientRect(); setAiPopover(p => p?.pinned ? p : { ...popoverData, x: r.left, y: r.bottom, pinned: false }); }}
                                                onMouseLeave={() => setAiPopover(p => p?.pinned ? p : null)}
                                                onClick={e => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setAiPopover(p => p?.pinned && p.key === key && p.qNo === qNo ? null : { ...popoverData, x: r.left, y: r.bottom, pinned: true }); }}
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: scoreColor + '18', border: '1px solid ' + scoreColor + '40', borderRadius: '12px', padding: '3px 9px', cursor: 'pointer', flexShrink: 0 }}
                                            >
                                                <img src={require('../../../../images/intelligentiaIcon.png')} alt="icon" className="w20px h20px mr0-5rem" />
                                                <span style={{ fontSize: '12px', fontWeight: 700, color: scoreColor }}>{scoreVal ? scoreVal : '?'}</span>
                                            </span>
                                        )}
                                        <Send data-tooltip={"Move"} className="cp" onClick={() => handleEditDispatch({ key, fileName: file.title, from: 'fileReport', actionType: "move", fileData: file, index })} />
                                        <TrashIcon data-tooltip={"Delete"} onClick={() => removeSectionFromImport(key, index)} className="cp f9" />
                                    </div>
                                </td>
                            )}
                        </tr>
                    </React.Fragment>;
                })}
            </>;
        } else {
            const { displayName, sectionOnly } = getSectionName(module, section, file, key);
            return <>
                <tr>
                    <td className={`tal fis-td mx1rem fs1rem fw600 bg1`}>
                        <span className="cp tdu" onClick={() => handleFilePreview(file)} data-tooltip="Click to preview file">{fileName}</span>
                    </td>
                    <td className={`tal fis-td mx1rem fs1rem fw600 bg1 fis-td`}>{sectionOnly}</td>
                    <td className={`tal fis-td mx1rem fs1rem fw600 bg1 fis-td`}>{displayName}</td>
                    <td className={`mx1rem fs1rem fw600 bg1 tal fis-td fis-td-action`}>
                        <EditIcon onClick={handleEditDispatch({ key, fileName })} />
                    </td>
                </tr>
            </>;
        }
    };

    const onClickReallocationComplete = async () => {
        if (!fileReport) return;
        const fileReportData = { ...fileReport, status: 'Complete', currentScore };
        const moduleName = tab && tab !== "" ? tab : (autoFillContext?.moduleType || '');
        const effectiveCompany = company || reduxCompany;
        const companyId = effectiveCompany?._id || autoFillContext?.companyId;
        const moduleId = autoFillContext?.moduleId;
        dispatch(setFileReport(fileReportData));
        dispatch(toggleModal({ open: 'autoFillStepper', autoFillStepperPhase: 'processing', percent: 0 }));
        const useModulesAllocate = !!moduleId;
        const resp = await postRequest(
            useModulesAllocate ? `/modules/allocateFiles` : `/company/allocateFiles`,
            useModulesAllocate
                ? { companyId, moduleId, fileReport: fileReportData }
                : { companyId, tab: moduleName, fileReport: fileReportData, fromAutoFill: !!autoFillContext?.enabled, sectionCode: autoFillContext?.sectionCode || null },
            true
        );
        if (!resp || resp.code === 400) return;
        if (resp.body?.report) dispatch(setFileReport(resp.body.report));
        const savedQuestions = resp.body?.savedQuestions;
        if (useModulesAllocate && savedQuestions?.length) {
            dispatch(updateFileAllocation({
                tempModuleAllocationPatch: { moduleId: String(moduleId), savedQuestions },
            }));
        }
        showMessage(resp['message']);
        if (modal.autoFillWithFilling) {
            handleSkipUpload();
        } else if (useModulesAllocate) {
            dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '', autoFillJustCompleted: true }));
        }
    };

    const onClickFileImportAuditLog = async (fileReportId) => {
        const res = await getRequest(`/company/getFileReportLogs/${fileReportId}`);
        setLogList(res['body']);
        dispatch(toggleModal({ open: 'fileReportAuditLog' }));
    };

    // ── Handlers ──

    const handleCancel = async () => {
        await postRequest('/clarence/auto-fill/cancel', {}).catch(() => { });
        dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '' }));
        showMessage('Auto-fill cancelled');
    };

    const handleMinimize = () => {
        const phaseMap = {
            uploading: 'uploading', indexing: 'indexing', allocation: 'allocated',
            processing: 'processingFiles', filling: 'filling',
        };
        dispatch(toggleModal({ open: '', autoFillStatusBarVisible: true, autoFillStatusBarPhase: phaseMap[phase] || phase }));
    };

    // ── File select step handlers ──
    const cloneFile = (file) => new File([file], file.name, { type: file.type });

    const handleFileSelect = async (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (selectedFiles.length === 0) return;
        const totalFiles = selectedFiles.length;
        let filesInfo = [];
        let relPaths = [];
        try {
            dispatch(toggleModal({
                open: 'autoFillStepper', autoFillStepperPhase: 'uploading',
                percent: 0, fileName: `Uploading ${totalFiles} file${totalFiles > 1 ? 's' : ''}...`, totalFiles, filesUploaded: 0,
                autoFillStatusBarPhase: 'uploading',
                autoFillUploadProgress: { current: 0, total: totalFiles, fileName: '' },
            }));
            relPaths = selectedFiles.map(f => f.webkitRelativePath || '');
            const attachment = selectedFiles.map(f => cloneFile(f));
            const companyId = autoFillContext?.companyId;
            const moduleType = autoFillContext?.moduleType;
            const moduleId = autoFillContext?.moduleId || null;
            const sectionCode = autoFillContext?.sectionCode;
            const scope2 = autoFillContext?.scope;
            const scopeName2 = autoFillContext?.scopeName || '';
            const isTempModule = !!moduleId;
            const uploadFileUrl = isTempModule ? '/modules/uploadFile' : '/company/uploadFile';
            const uploadFilesUrl = isTempModule ? '/modules/uploadFiles' : '/company/uploadFiles';
            const res = await postRequest(uploadFileUrl, isTempModule ? { attachment, companyId, moduleId, moduleType, totalFiles, sectionCode: sectionCode || '', scope: scope2, scopeName: scopeName2 } : { attachment, companyId, moduleType, totalFiles, sectionCode: sectionCode || '', scope: scope2, scopeName: scopeName2 }, true);
            if (res?.body?.reports) filesInfo = res.body.reports;
            else if (res?.body?.report) filesInfo = [res.body.report];
            e.target.value = '';
            dispatch(toggleModal({ open: 'autoFillStepper', autoFillStepperPhase: 'indexing', autoFillStatusBarPhase: 'indexing', autoFillStatusBarVisible: true }));
            const uploadFilesRes = await postRequest(uploadFilesUrl, isTempModule ? { files: filesInfo, companyId, moduleId, tab: moduleType, relPaths, fromAutoFill: true, ...(scope2 === 'section' && sectionCode ? { sectionCode } : {}) } : { files: filesInfo, companyId, tab: moduleType, relPaths, fromAutoFill: !!autoFillContext?.enabled, ...(scope2 === 'section' && sectionCode ? { sectionCode } : {}) }, true);
            if (uploadFilesRes?.body?.cancelled) {
                dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '' }));
                return;
            }
            if (!uploadFilesRes?.body?.report) {
                dispatch(toggleModal({ open: '', autoFillStatusBarPhase: 'error' }));
                showMessage('File upload failed');
                return;
            }
            const fileReportResult = uploadFilesRes.body.report;
            const allocated = Object.keys(fileReportResult.importResult || {}).filter(
                key => fileReportResult.importResult[key]?.section?.some(el => !el.includes("DXXX") && !el.includes("SXXX"))
            ).length;
            const total = Object.keys(fileReportResult.importResult || {}).length;
            const score = total === 0 ? 0 : Math.round(allocated / total * 100);
            dispatch(updateFileAllocation({ fileReport: fileReportResult, currentScore: score, tab: moduleType, autoFillContext: { ...autoFillContext } }));
            const isMinimizedNow = store.getState().modal.autoFillStatusBarVisible;
            dispatch(toggleModal({ ...(isMinimizedNow ? {} : { open: 'autoFillStepper' }), autoFillStepperPhase: 'allocation', autoFillStatusBarVisible: false, autoFillStatusBarPhase: 'allocated' }));
            showMessage(uploadFilesRes.message || 'Files allocated - tap to review');
        } catch (error) {
            console.error('Upload error:', error);
            dispatch(toggleModal({ open: '' }));
            showMessage('Upload failed. Please try again.');
            e.target.value = '';
        }
    };

    const handleSkipUpload = () => {
        const companyId = autoFillContext?.companyId;
        const moduleType = autoFillContext?.moduleType;
        dispatch(toggleModal({
            open: 'autoFillStepper', autoFillStepperPhase: 'filling',
            autoFillModuleName: modal.autoFillModuleName || '',
            autoFillTotal: 0, autoFillCurrent: 0, autoFillCurrentQuestion: '',
            autoFillCompleted: false, autoFillProcessed: 0, autoFillAnswered: 0,
            autoFillNeedsReview: 0, autoFillSkipped: 0, autoFillStatusBarPhase: 'filling',
        }));
        const moduleId = autoFillContext?.moduleId || null;
        const payload = { companyId, moduleType, minConfidence: 0.7, ...(moduleId ? { moduleId } : {}) };
        const scope2 = autoFillContext?.scope;
        const sectionCode = autoFillContext?.sectionCode;
        if (scope2 === 'section' && sectionCode) { payload.sectionCode = sectionCode; }
        postRequest('/clarence/auto-fill', payload).then(response => {
            if (response?.code === 200) {
                const { answered, needsReview, skipped, processed } = response?.body || {};
                dispatch(toggleModal({ open: 'autoFillStepper', autoFillStepperPhase: 'result', autoFillCompleted: true, autoFillProcessed: processed || 0, autoFillAnswered: answered || 0, autoFillNeedsReview: needsReview || 0, autoFillSkipped: skipped || 0, autoFillStatusBarPhase: 'completed' }));
            } else {
                dispatch(toggleModal({ open: '', autoFillStatusBarPhase: 'error' }));
                showMessage(response?.message || 'Auto-fill failed');
            }
        }).catch(() => {
            dispatch(toggleModal({ open: '', autoFillStatusBarPhase: 'error' }));
            showMessage('Auto-fill failed. Please try again.');
        });
    };

    if (!isOpen) return null;

    const showCancelMinimize = !['select', 'result', 'allocation'].includes(phase);

    return (
        <Modal open={isOpen} onClose={() => { if (phase === 'result') dispatch(toggleModal({ open: '', autoFillJustCompleted: true })); }} className="backdrop td500 zi4 pf t0 l0 w100vw h100vh df jcc aic">
            <>
                <div className="pr bg1 shadow w100 bsbb df fdc m1rem br5px" style={{ maxWidth: '1450px', maxHeight: '92vh', overflowY: 'hidden', padding: '0 2rem 2rem' }}>

                    {/* Header */}
                    <div style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2, paddingTop: '1.5rem', paddingBottom: '0.5rem' }}>
                        <div className="df aic jcsb mb0-5rem">
                            <div className="df aic" style={{ gap: '0.5rem' }}>
                                <img src={require('../../../../images/dataRepo/file_placement.svg')} alt="auto-fill" style={{ width: '28px', height: '28px' }} />
                                <h2 className="c15 fw600 fs18px mb0rem">Filing Assistant</h2>
                            </div>
                            {phase === 'result' && (
                                <button onClick={() => dispatch(toggleModal({ open: '' }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>✕</button>
                            )}
                        </div>
                        {headerInfo && modal.autoFillWithFilling && (
                            <p className="fs12px c14 fw500 mb0-5rem" style={{ marginLeft: '2.25rem' }}>{headerInfo}</p>
                        )}
                        <AutoFillStepper currentStep={phase} uploadOnly={!modal.autoFillWithFilling} />
                        <Divider style={{ marginTop: '8px' }} />
                    </div>

                    {/* ── Step 1: Select Files ── */}
                    {phase === 'select' && (
                        <div className="df fdc aic jcc" style={{ padding: '3rem 1rem', gap: '1.5rem', minHeight: '450px' }}>
                            <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div className="tac">
                                {modal.autoFillWithFilling
                                    ? <h3 className="c15 fw600 fs18px mb0-5rem">Would you like to upload documents?</h3>
                                    : <h3 className="c15 fw600 fs18px mb0-5rem">{modal.autoFillUploadMode === 'folders' ? 'Select folders to import' : 'Select files to import'}</h3>
                                }
                                {modal.autoFillWithFilling && <p className="c27 fs14px mb0-25rem">Uploading relevant documents can improve auto-fill accuracy.</p>}
                                {modal.autoFillUploadMode !== 'folders' && <p className="fs12px" style={{ color: '#9CA3AF' }}>Supported: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT</p>}
                            </div>
                            <div className="df aic jcc" style={{ gap: '1rem', marginTop: '0.5rem' }}>
                                <button
                                    onClick={() => dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '' }))}
                                    style={{ background: 'none', border: '1.5px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', color: '#6B7280', fontSize: '14px', fontWeight: 500, padding: '0.6rem 1.8rem' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{ background: '#fc7785', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff', fontSize: '14px', fontWeight: 600, padding: '0.6rem 1.8rem' }}
                                >
                                    {modal.autoFillUploadMode === 'folders' ? 'Select Folders' : 'Upload Documents'}
                                </button>
                                {modal.autoFillWithFilling && (
                                    <button
                                        onClick={handleSkipUpload}
                                        style={{ background: 'none', border: '1.5px solid #9CA3AF', borderRadius: '8px', cursor: 'pointer', color: '#6B7280', fontSize: '14px', fontWeight: 500, padding: '0.6rem 1.8rem' }}
                                    >
                                        Skip, Use Existing Documents
                                    </button>
                                )}
                            </div>
                            {modal.autoFillUploadMode === 'folders'
                                ? <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: 'none' }} webkitdirectory="" directory="" mozdirectory="" />
                                : <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" />
                            }
                        </div>
                    )}

                    {/* ── Step 2: Files Uploading ── */}
                    {phase === 'uploading' && (
                        <div className="df fdc aic jcc" style={{ padding: '1rem 0' }}>
                            <div className="pr" style={{ display: 'inline-block' }}>
                                <img src={require('../../../../images/loaders/pc-to-data-clarence.gif')} alt="uploading" style={{ maxHeight: '180px', display: 'block' }} />
                                <div className="df jcsb" style={{ width: '100%', marginTop: '4px' }}>
                                    <p className="fs12px c43" style={{ textAlign: 'center', width: '30%' }}>Your Computer</p>
                                    <p className="fs12px c43" style={{ textAlign: 'center', width: '30%' }}>Clarence</p>
                                </div>
                            </div>
                            <div className="maw70 w700px df fdc jcc aic mt0rem">
                                <h1 className="c15 fs24px w100 tac fw500">Uploading Files</h1>
                                <div className="w100 df jcc aic mt1rem">
                                    <progress color="green" className="maw90 w90 mr1rem" style={{ border: 'none' }} id="upload-progress-bar" value={modal.percent || 0} max="100" />
                                </div>
                                {modal.fileName && (
                                    <div className="df aic jcc">
                                        <span className="c15 fw500 fs13px my0-5rem">{modal.fileName} | {modal.filesUploaded} / {modal.totalFiles}</span>
                                    </div>
                                )}
                                <p className="c15 fs1rem w100 tac fw400 my2rem">Almost there</p>
                                <div className="df aic jcc" style={{ gap: '1rem' }}>
                                    <button onClick={handleCancel} style={btnCancel}>Cancel</button>
                                    <button onClick={handleMinimize} style={btnMinimize}>Minimize</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Step 3: Indexing & Analyzing ── */}
                    {phase === 'indexing' && (
                        <div className="df fdc aic" style={{ padding: '1rem 0' }}>
                            <div className="AnimateWiz tac">
                                <img className="h15rem mla mra" src={require('../../../../images/wizard.gif')} alt="animWiz" />
                            </div>
                            {allocationProgress.total > 0 ? (
                                <>
                                    <h2 className="fs1-5rem-sm w100 tac mb1rem c15">Allocating Files to Sections...</h2>
                                    <div style={{ width: '100%', maxWidth: '520px', margin: '0 auto', textAlign: 'center' }}>
                                        <p className="fs0-85rem c27 mb0-5rem">{allocationProgress.current} of {allocationProgress.total} files matched</p>
                                        {allocationProgress.fileName && <p className="fs0-85rem c27" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '0 0 0.25rem' }}>File: <span className="c15 fw500">{allocationProgress.fileName}</span></p>}
                                        {allocationProgress.matchedQNo && allocationProgress.matchedQNo !== 'SXXX' && allocationProgress.matchedQNo !== 'DXXX' ? (
                                            <>
                                                <p className="fs0-85rem" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', margin: '0 0 0.2rem', color: '#fc7785' }}>→ {allocationProgress.matchedQNo} — {allocationProgress.matchedQuestion}{allocationProgress.confidence ? ` (${Math.round(allocationProgress.confidence * 100)}%)` : ''}</p>
                                                {allocationProgress.reasoning && <p className="fs0-75rem c27 tac" style={{ maxWidth: '480px', margin: '0 auto', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{allocationProgress.reasoning}</p>}
                                            </>
                                        ) : allocationProgress.matchedQNo && <p className="fs0-85rem c27" style={{ margin: 0 }}>→ Unallocated</p>}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2 className="fs1-5rem-sm w100 tac mb1rem c15">Indexing & Analysing Files...</h2>
                                    <div style={{ width: '100%', maxWidth: '520px', margin: '0 auto' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                                            <span className="fs0-75rem c27" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                                                {indexingProgress.currentFile ? (indexingProgress.current < indexingProgress.total ? `Reading: ${indexingProgress.currentFile}` : '✓ All files indexed') : 'Preparing files...'}
                                            </span>
                                            <span className="fs0-75rem c27">{indexingProgress.total > 0 ? `${indexingProgress.current || 0}/${indexingProgress.total}` : ''}</span>
                                        </div>
                                        <div style={{ background: '#E5E7EB', borderRadius: '10px', height: '8px', overflow: 'hidden' }}>
                                            <div style={{ background: '#22c55e', height: '100%', borderRadius: '10px', width: indexingProgress.total > 0 ? `${Math.round(((indexingProgress.current || 0) / indexingProgress.total) * 100)}%` : '0%', transition: 'width 0.3s ease' }} />
                                        </div>
                                    </div>
                                </>
                            )}
                            <p className="fs0-75rem c27 tac mt1rem" style={{ opacity: 0.7 }}>You can minimise this window — processing will continue in the background.</p>
                            <div className="df aic jcc" style={{ gap: '1rem', marginTop: '0.75rem' }}>
                                <button onClick={handleCancel} style={btnCancel}>Cancel</button>
                                <button onClick={handleMinimize} style={btnMinimize}>Minimize</button>
                            </div>
                        </div>
                    )}

                    {/* ── Step 4: Allocation ── */}
                    {phase === 'allocation' && fileReport && (
                        <div className="df pr2rem" style={{ gap: '1.5rem', marginTop: '1rem' }}>
                            <div style={{ flex: previewFile ? '0 0 50%' : '1 1 100%', minWidth: 0, transition: 'flex 0.3s ease' }}>
                                <div className="df jcsb aic mb1rem">
                                    <div className="df jcfs aic">
                                        <img src={require('../../../../images/dataRepo/file_placement.svg')} alt="fileManagement" />
                                        <h1 className="c15 fw600 fs18px mb0rem ml0-5rem">Filing Assistant</h1>
                                    </div>
                                    <button onClick={() => onClickFileImportAuditLog(fileReport['_id'])} className="cp tac c1 bg5 c15 br20px px1-5rem py0-5rem fw500 fs13px">Log</button>
                                </div>
                                <div className="dg gg0-5rem mb2rem fs1rem oya" style={{ maxHeight: 'calc(92vh - 460px)' }}>
                                    <table className="fis bcc">
                                        <thead>
                                            <tr>
                                                <th className="tal fs14px fw400 c37" style={{ width: '25%' }}>Files</th>
                                                <th className="tal fs14px fw400 c37" style={{ width: '30%' }}>Section Name</th>
                                                <th className="tal fs14px fw400 c37" style={{ width: '35%' }}>Allocated Section</th>
                                                {fileReport['status'] == 'In Progress' && <th className="fs14px fw400 c37" style={{ width: '10%' }}>Actions</th>}
                                            </tr>
                                        </thead>
                                        {fileReport.importResult && Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => !el.includes("DXXX") && !el.includes("SXXX"))).length > 0 && (
                                            <tbody>
                                                <tr><td><h1 className="c15 fs18px fw500 tal">Allocated files</h1></td></tr>
                                                {Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => !el.includes("DXXX") && !el.includes("SXXX"))).map(getImportResultEntryHTML)}
                                            </tbody>
                                        )}
                                    </table>
                                    {fileReport.importResult && Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => el.includes("DXXX") || el.includes("SXXX"))).length > 0 && (<>
                                        <h1 className="c15 fs18px fw500 tal mt1rem ml1rem">Unallocated Bin: Please reallocate files <span className="c4">*</span></h1>
                                        <table className="fis bcc">
                                            <thead className="dn">
                                                <tr>
                                                    <th className="fis-th w40 bbn"></th>
                                                    <th className="fis-th bbn"></th>
                                                    <th className="fis-th w60 bbn"></th>
                                                    {fileReport['status'] == 'In Progress' && <th className="fis-th fis-th-action bbn"></th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => el.includes("DXXX") || el.includes("SXXX"))).map(getImportResultEntryHTML)}
                                            </tbody>
                                        </table>
                                    </>)}
                                </div>
                                <div className="df w100 fdr jcc">
                                    {fileReport['status'] == 'In Progress' && fileReport.importResult && Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => !el.includes("DXXX") && !el.includes("SXXX"))).length > 0 && (
                                        <button onClick={onClickReallocationComplete} className="bg3 bg4-hover c1 br10px px3rem py0-75rem cp fw500 fs1rem">Confirm Allocation</button>
                                    )}
                                </div>
                                <div>
                                    <div style={{ width: '60%', height: '1px', backgroundColor: '#82828256', opacity: '64%', margin: '24px auto' }}></div>
                                    <div className="df aic jcc">
                                        <Search className="mr0-5rem" />
                                        <p className="fs13px c37 fw400 mr0-5rem">{fileReport.importResult && (<>[{Object.keys(fileReport.importResult).length}] imported files = [{Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => !el.includes("DXXX") && !el.includes("SXXX"))).length}] allocated files + [{Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => el.includes("DXXX") || el.includes("SXXX"))).length}] unallocated bin files</>)}</p>
                                        <p className="fs13px c37 fw400">Allocation accuracy {currentScore}%</p>
                                    </div>
                                    <div className="df aic jcc" style={{ gap: '1rem', marginTop: '0.5rem' }}>
                                        <button onClick={handleCancel} style={btnCancel}>Cancel</button>
                                        <button onClick={handleMinimize} style={btnMinimize}>Minimize</button>
                                    </div>
                                </div>
                            </div>
                            {previewFile && (
                                <div style={{ flex: '0 0 48%', minWidth: 0, display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e0e0e0', paddingLeft: '1rem' }}>
                                    <div className="df jcsb aic" style={{ marginBottom: '0.75rem' }}>
                                        <div className="df aic" style={{ minWidth: 0 }}>
                                            <VisibleIcon style={{ marginRight: '0.5rem', color: '#6B7280', flexShrink: 0 }} />
                                            <h3 className="c15 fw600 fs14px mb0rem" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewFile.name}</h3>
                                        </div>
                                        <div className="df aic" style={{ flexShrink: 0, marginLeft: '0.5rem' }}>
                                            <a href={previewFile.url} target="_blank" rel="noopener noreferrer" className="cp mr0-5rem fs12px c36 fw500 tdu">Open</a>
                                            <CloseIcon className="cp" style={{ fontSize: '1rem', color: '#6B7280' }} onClick={() => setPreviewFile(null)} />
                                        </div>
                                    </div>
                                    <div style={{ flex: 1, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0', backgroundColor: '#f9fafb', minHeight: '400px' }}>
                                        <iframe src={previewFile.url} title={`Preview: ${previewFile.name}`} style={{ width: '100%', height: '100%', border: 'none', minHeight: '400px' }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Step 5: Saving & Indexing ── */}
                    {phase === 'processing' && (
                        <div className="df fdc aic jcc" style={{ padding: '1rem 0' }}>
                            <div className="pr" style={{ display: 'inline-block' }}>
                                <img src={require('../../../../images/loaders/clarence-to-data-repository.gif')} alt="processing" style={{ maxHeight: '180px', display: 'block' }} />
                                <div className="df jcsb px2rem mb1rem" style={{ width: '100%', marginTop: '0.25rem' }}>
                                    <p className="fs12px c43" style={{ textAlign: 'left' }}>Clarence</p>
                                    <p className="fs12px c43" style={{ textAlign: 'right' }}>Data Repository</p>
                                </div>
                            </div>
                            <div className="maw70 w700px df fdc jcc aic mt0rem">
                                <h1 className="c15 fs24px w100 tac fw500">Saving files and linking them to your answers...</h1>
                                <div className="w100 mt1rem" style={{ maxWidth: '520px' }}>
                                    <div className="df jcsb mb0-25rem">
                                        <span className="fs0-75rem c27" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>
                                            {savingProgress.fileName ? `Saving: ${savingProgress.fileName}` : 'Preparing...'}
                                        </span>
                                        <span className="fs0-75rem c27">{savingProgress.total > 0 ? `${savingProgress.current}/${savingProgress.total}` : ''}</span>
                                    </div>
                                    <div style={{ background: '#E5E7EB', borderRadius: '10px', height: '8px', overflow: 'hidden' }}>
                                        <div style={{ background: '#22c55e', height: '100%', borderRadius: '10px', width: savingProgress.total > 0 ? `${Math.round((savingProgress.current / savingProgress.total) * 100)}%` : '0%', transition: 'width 0.3s ease' }} />
                                    </div>
                                    {savingProgress.qNo && <p className="fs0-75rem tac mt0-5rem" style={{ color: '#4a67ff' }}>→ Linked to: {savingProgress.qNo}</p>}
                                </div>
                                <div className="df aic jcc mt1-5rem" style={{ gap: '1rem' }}>
                                    <button onClick={handleCancel} style={btnCancel}>Cancel</button>
                                    <button onClick={handleMinimize} style={btnMinimize}>Minimize</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Step 6: Filling ── */}
                    {phase === 'filling' && (
                        <div className="df fdc aic" style={{ padding: '1rem 0' }}>
                            <div className="AnimateWiz tac">
                                <img className="h15rem mla mra" src={require('../../../../images/wizard.gif')} alt="animWiz" />
                            </div>
                            <h2 className="fs1-5rem-sm w100 tac mb2rem c15">Auto-Filling Questions...</h2>
                            {modal.autoFillModuleName && <p className="tac c14 fw600 fs1rem mb1rem" style={{ marginTop: '-1rem' }}>{modal.autoFillModuleName}</p>}
                            <div className="tac w100">
                                <div className="progress-area w60 mla mra mb1rem">
                                    <div className="w100 df jcc aic">
                                        {modal.autoFillTotal > 0 ? (
                                            <progress color="green" className="maw90 w90" style={{ border: 'none' }} value={modal.autoFillCurrent || 0} max={modal.autoFillTotal} />
                                        ) : (
                                            <progress color="green" className="maw90 w90" style={{ border: 'none' }} />
                                        )}
                                    </div>
                                </div>
                                <p className="c15 fw500 fs13px mb0-5rem">
                                    {modal.autoFillTotal > 0 ? `Processing ${modal.autoFillCurrent || 0} of ${modal.autoFillTotal} questions (${Math.round(((modal.autoFillCurrent || 0) / modal.autoFillTotal) * 100)}%)` : 'Analyzing questions...'}
                                </p>
                                {(modal.autoFillSectionName || modal.autoFillQuestionText || modal.autoFillReadingFiles?.length > 0) && (
                                    <div style={{ maxWidth: '520px', margin: '0.75rem auto 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem', textAlign: 'left' }}>
                                        {modal.autoFillSectionName && (
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.25rem', fontSize: '13px', color: '#111827' }}>
                                                <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Section:</span>
                                                <span style={{ wordBreak: 'break-word' }}>{modal.autoFillSectionNo ? `${modal.autoFillSectionNo} — ${modal.autoFillSectionName}` : modal.autoFillSectionName}</span>
                                            </div>
                                        )}
                                        {modal.autoFillQuestionText && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '13px', color: '#111827', lineHeight: '1.5', overflow: 'hidden' }}>
                                                <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Question:</span>
                                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{modal.autoFillQuestionText}</span>
                                            </div>
                                        )}
                                        {modal.autoFillReadingFiles?.length > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.25rem', fontSize: '12px', color: '#111827', lineHeight: '1.6' }}>
                                                <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Reading:</span>
                                                <span style={{ wordBreak: 'break-word' }}>{modal.autoFillReadingFiles.join(', ')}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {modal.autoFillStartTime && modal.autoFillCurrent > 0 && modal.autoFillTotal > 0 && (() => {
                                    const remaining = ((Date.now() - modal.autoFillStartTime) / modal.autoFillCurrent) * (modal.autoFillTotal - modal.autoFillCurrent);
                                    const eta = remaining > 60000 ? `~${Math.ceil(remaining / 60000)} min remaining` : `~${Math.ceil(remaining / 1000)} sec remaining`;
                                    return <p className="c27 fs12px mb1rem">{eta}</p>;
                                })()}
                                <div className="df aic jcc" style={{ gap: '1rem', marginTop: '1rem' }}>
                                    <button onClick={handleCancel} style={btnCancel}>Cancel</button>
                                    <button onClick={handleMinimize} style={btnMinimize}>Minimize</button>
                                </div>
                                <p className="c27 mt1rem">We're using AI to find answers from your documents.<br />Please wait while the magic happens!</p>
                            </div>
                        </div>
                    )}

                    {/* ── Step 7: Result ── */}
                    {phase === 'result' && (
                        <div className="df fdc aic" style={{ padding: '1rem 0' }}>
                            <div className="AnimateWiz tac">
                                <img className="h15rem mla mra" src={require('../../../../images/wizard.gif')} alt="animWiz" />
                            </div>
                            <h2 className="fs1-5rem-sm w100 tac mb2rem c15">Questions Auto-Fill Complete!</h2>
                            {modal.autoFillModuleName && <p className="tac c14 fw600 fs1rem mb1rem" style={{ marginTop: '-1rem' }}>{modal.autoFillModuleName}</p>}
                            <div className="tac">
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxWidth: '320px', margin: '0 auto 2rem' }}>
                                    <div style={{ background: '#F3F4F6', borderRadius: '12px', padding: '1rem', textAlign: 'center' }}>
                                        <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1F2937' }}>{modal.autoFillProcessed || 0}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>Processed</div>
                                    </div>
                                    <div style={{ background: '#D1FAE5', borderRadius: '12px', padding: '1rem', textAlign: 'center' }}>
                                        <div style={{ fontSize: '2rem', fontWeight: '700', color: '#059669' }}>{modal.autoFillAnswered || 0}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.25rem' }}>Answered</div>
                                    </div>
                                    <div style={{ background: '#FEF3C7', borderRadius: '12px', padding: '1rem', textAlign: 'center' }}>
                                        <div style={{ fontSize: '2rem', fontWeight: '700', color: '#D97706' }}>{modal.autoFillNeedsReview || 0}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#D97706', marginTop: '0.25rem' }}>Needs Review</div>
                                    </div>
                                    <div style={{ background: '#F3F4F6', borderRadius: '12px', padding: '1rem', textAlign: 'center' }}>
                                        <div style={{ fontSize: '2rem', fontWeight: '700', color: '#6B7280' }}>{modal.autoFillSkipped || 0}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>Skipped</div>
                                    </div>
                                </div>
                                <button onClick={() => dispatch(toggleModal({ open: '', autoFillStatusBarVisible: false, autoFillStatusBarPhase: '', autoFillStepperPhase: '', autoFillJustCompleted: true }))} className="bg3 bg4-hover c1 br10px px3rem py0-75rem cp fw500 fs1rem">Close</button>
                            </div>
                        </div>
                    )}

                </div>

                {/* Girl image — allocation step only */}
                {phase === 'allocation' && fileReport && (
                    <div style={{ position: 'relative' }}>
                        {fileReport.importResult && Object.keys(fileReport.importResult).filter(key => fileReport.importResult[key]['section'].some(el => el.includes("DXXX") || el.includes("SXXX"))).length > 0
                            ? <img src={require('../../../../images/dataRepo/sorryMessage.png')} alt="messageLadie" className="animateRight" />
                            : <img src={require('../../../../images/dataRepo/fillingassistantGirl.png')} alt="messageLadie" className="animateRight" />
                        }
                    </div>
                )}
                {/* AI Confidence Popover */}
                {aiPopover && (
                    <div onClick={() => { if (aiPopover.pinned) setAiPopover(null); }} style={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: aiPopover.pinned ? 'auto' : 'none' }}>
                        <div
                            onClick={e => e.stopPropagation()}
                            style={{
                                position: 'fixed',
                                top: Math.max(8, aiPopover.y - 280),
                                left: Math.min(Math.max(aiPopover.x - 260, 8), window.innerWidth - 296),
                                width: '280px',
                                background: '#fff',
                                borderRadius: '12px',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                                padding: '1rem',
                                zIndex: 10000,
                                border: '1px solid #E5E7EB',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <img src={require('../../../../images/intelligentiaIcon.png')} alt="icon" className="w20px h20px mr0-5rem" />
                                    <span style={{ fontWeight: 700, fontSize: '13px', color: '#111827' }}>{aiPopover.isUnallocated ? 'Why Unallocated?' : 'AI Confidence'}</span>
                                </div>
                                <span onClick={() => setAiPopover(null)} style={{ cursor: 'pointer', color: '#9CA3AF', fontSize: '14px', lineHeight: 1 }}>✕</span>
                            </div>
                            <div style={{ height: '1px', background: '#F3F4F6', marginBottom: '0.75rem' }} />
                            <div style={{ marginBottom: '0.75rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                    <span style={{ fontSize: '12px', color: '#6B7280' }}>{aiPopover.isUnallocated ? 'Confidence score' : 'Match score'}</span>
                                    <span style={{ fontSize: '13px', fontWeight: 700, color: aiPopover.scoreColor }}>{aiPopover.scoreVal ? aiPopover.scoreVal : '—'}{aiPopover.scoreLabel ? ' — ' + aiPopover.scoreLabel : ''}</span>
                                </div>
                                <div style={{ height: '6px', borderRadius: '99px', background: '#F3F4F6', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${(aiPopover.scoreVal || 0) * 10}%`, background: aiPopover.scoreColor, borderRadius: '99px', transition: 'width 0.4s ease' }} />
                                </div>
                            </div>
                            <div style={{ height: '1px', background: '#F3F4F6', marginBottom: '0.75rem' }} />
                            {aiPopover.isUnallocated ? (
                                <div>
                                    <p style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>AI Reasoning</p>
                                    <p style={{ fontSize: '12px', color: '#374151', lineHeight: 1.5, margin: aiPopover.suggestedQNo ? '0 0 8px' : 0 }}>
                                        {aiPopover.fileName && <span style={{ fontWeight: 700, wordBreak: 'break-all' }}>{aiPopover.fileName}</span>}
                                        {aiPopover.fileName && ' - '}
                                        {aiPopover.reasoning || 'Confidence too low to allocate automatically.'}
                                    </p>
                                    {aiPopover.suggestedQNo && (
                                        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '7px', padding: '6px 10px' }}>
                                            <p style={{ fontSize: '11px', fontWeight: 600, color: '#1D4ED8', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Possible match</p>
                                            <p style={{ fontSize: '12px', color: '#1E40AF', lineHeight: 1.5, margin: 0 }}>
                                                {aiPopover.suggestedQNo}{aiPopover.suggestedSection ? ' — ' + aiPopover.suggestedSection : ''}{aiPopover.suggestedQuestion ? ': ' + aiPopover.suggestedQuestion : ''}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <p style={{ fontSize: '11px', fontWeight: 600, color: '#6B7280', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Why this section?</p>
                                    <p style={{ fontSize: '12px', color: '#374151', lineHeight: 1.5, margin: 0 }}>
                                        {aiPopover.fileName && <span style={{ fontWeight: 700, wordBreak: 'break-all' }}>{aiPopover.fileName}</span>}
                                        {aiPopover.fileName && ' - '}
                                        {aiPopover.reasoning || 'No reasoning provided.'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </>
        </Modal>
    );
};

export default AutoFillStepperModal;
